import PropTypes from 'prop-types';
import React, {Component} from 'react';
import {SVGIcon} from '@homeaway/react-svg';
import {CHEVRON_DOWN__12} from '@homeaway/svg-defs';
import DropdownItem from '../DropdownItem/DropdownItem';
import {Button} from '@homeaway/react-buttons';
import classNames from 'classnames';
import {ArrowKeysNavigation} from '@homeaway/a11y-tools';
import './DropdownBase.less';

export default class DropdownBase extends Component {
    static propTypes = {
        /** id attribute of the Dropdown. Used for aria-labeledby value of dropdown list. */
        id: PropTypes.string.isRequired,
        /** Text to display on the Dropdown button. */
        label: PropTypes.node.isRequired,
        /** Array of links to generate.  Shape contains: `link`, `text`, and `cssClass`. */
        links: PropTypes.arrayOf(
            PropTypes.shape({
                link: PropTypes.string,
                text: PropTypes.string,
                className: PropTypes.string
            })
        ),
        /** Flag to control if the Dropdown is open via an external state management. */
        open: PropTypes.bool,
        /** Child content of Dropdown button if not using standard label prop. */
        buttonChildren: PropTypes.node,
        /** Render the toggle as a custom React component. */
        customToggle: PropTypes.element,
        /** Flag to specify that the button label is Screen Reader only. Usually used with props.buttonChildren. */
        srOnlyLabel: PropTypes.bool,
        /** Flag to keep the arrow icon from appearing to the right of the text. */
        noArrow: PropTypes.bool,
        /** Flag to keep have the dropdown menu open from the right. */
        dropdownRight: PropTypes.bool,
        /** callback for when the dropdown toggle is clicked */
        onDropdownClick: PropTypes.func,
        /** callback for when the dropdown contents are clicked - can be used to close the dropdown after selecting an item, for example */
        onDropdownContentClick: PropTypes.func,
        /** callback for touch devices tapping off of the dropdown / dropdown list */
        onTouch: PropTypes.func,
        /** callback for when the dropdown / dropdown list loses focus */
        onBlur: PropTypes.func,
        /** Set max height of the dropdown menu */
        maxHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        /** A11y role, use role 'menu' to enable arrow keys navigation. */
        role: PropTypes.string
    };

    static defaultProps = {
        maxHeight: '',
        role: 'dialog'
    };

    constructor(props) {
        super(props);
        this.arrowNavigation = null;
    }

    componentDidMount() {
        const {role, id} = this.props;

        if (role === 'menu') {
            this.arrowNavigation = new ArrowKeysNavigation(`${id}--container`);
            this.arrowNavigation.bindEvents();
        }
    }

    /* eslint-disable complexity */
    componentDidUpdate(prevProps) {
        if (!prevProps.open && this.props.open) {
            this.addWindowListeners();
            if (this.arrowNavigation) {
                this.arrowNavigation.bindEvents();
            }
        }
        if (prevProps.open && !this.props.open) {
            this.removeWindowListeners();
            if (this.arrowNavigation) {
                this.arrowNavigation.unbindEvents();
            }
        }
    }

    componentWillUnmount() {
        this.removeWindowListeners();
        if (this.arrowNavigation) {
            this.arrowNavigation.unbindEvents();
            this.arrowNavigation = null;
        }
    }

    handleTabKeyPress = (e) => {
        if (e.keyCode === 9) {
            this.props.onBlur();
            return true;
        }

        return false;
    };

    renderToggle = () => {
        const {customToggle} = this.props;

        if (customToggle) {
            return this.renderCustomToggle();
        }

        return this.renderDefaultToggle();
    };

    renderDefaultToggle = () => {
        const {
            id,
            label,
            onDropdownClick,
            buttonChildren,
            srOnlyLabel,
            open,
            noArrow
        } = this.props;
        const toggleClasses = classNames(
            'Dropdown__toggle',
            'border-border-color',
            'border--contrast',
            {
                'Dropdown__toggle--open': open
            }
        );
        const toggleIconClasses = classNames('Dropdown__toggle-icon', {
            'Dropdown__toggle-icon--open': open
        });

        return (
            <Button
                id={id}
                onClick={onDropdownClick}
                aria-haspopup="true"
                label={label}
                aria-expanded={open}
                srOnlyLabel={srOnlyLabel}
                className={toggleClasses}
            >
                {buttonChildren}
                {!noArrow && (
                    <SVGIcon
                        className={toggleIconClasses}
                        markup={CHEVRON_DOWN__12}
                    />
                )}
            </Button>
        );
    };

    renderCustomToggle = () => {
        const {id, onDropdownClick, customToggle, open} = this.props;

        const clone = React.cloneElement(customToggle, {
            onClick: onDropdownClick,
            'aria-haspopup': true,
            'aria-expanded': open,
            id
        });

        return clone;
    };

    blurHandler = (e) => {
        const isDocument = typeof document !== 'undefined';
        const currentTarget = e.currentTarget;
        const relatedTarget =
            e.relatedTarget ||
            (isDocument && document.hasFocus() && document.activeElement) ||
            null;

        if (!currentTarget.contains(relatedTarget)) {
            this.props.onBlur();
        }
    };

    windowClickHandler = (e) => {
        const {open, onBlur, onTouch} = this.props;
        if (
            open &&
            this.rootElem &&
            e &&
            e.target &&
            !this.rootElem.contains(e.target)
        ) {
            if (e.type === 'mouseup') {
                onBlur();
            } else {
                onTouch();
            }
        }
    };

    addWindowListeners = () => {
        if (typeof window !== 'undefined') {
            window.document.addEventListener(
                'mouseup',
                this.windowClickHandler
            );
            if ('ontouchstart' in window) {
                window.document.addEventListener(
                    'touchstart',
                    this.windowClickHandler
                );
            }

            if (this.arrowNavigation) {
                window.document.addEventListener(
                    'keydown',
                    this.handleTabKeyPress
                );
            }
        }
    };

    removeWindowListeners = () => {
        if (typeof window !== 'undefined') {
            window.document.removeEventListener(
                'mouseup',
                this.windowClickHandler
            );

            if ('ontouchstart' in window) {
                window.document.removeEventListener(
                    'touchstart',
                    this.windowClickHandler
                );
            }

            if (this.arrowNavigation) {
                window.document.removeEventListener(
                    'keydown',
                    this.handleTabKeyPress
                );
            }
        }
    };

    setRootElem = (elem) => {
        this.rootElem = elem;
    };

    render() {
        const {
            id,
            children,
            links,
            className,
            dropdownRight,
            open,
            maxHeight,
            role,
            onDropdownContentClick
        } = this.props;
        const dropdownClasses = classNames(
            'Dropdown',
            {
                'Dropdown--open': open
            },
            className
        );
        const menuClasses = classNames('Dropdown__menu', {
            'Dropdown__menu--open': open,
            'Dropdown__menu--right': dropdownRight,
            'Dropdown__menu--overflow': !!maxHeight
        });

        return (
            <div
                className={dropdownClasses}
                onBlur={this.blurHandler}
                tabIndex="-1"
                ref={this.setRootElem}
                role="presentation"
            >
                {this.renderToggle()}
                {/* eslint-disable-next-line */}
                <ul
                    className={menuClasses}
                    style={{maxHeight}}
                    role={role}
                    aria-labelledby={id}
                    id={`${id}--container`}
                    onClick={onDropdownContentClick}
                >
                    {links &&
                        links.map((link, i) => {
                            return (
                                <DropdownItem
                                    key={i}
                                    dropdownRole={role}
                                    {...link}
                                />
                            );
                        })}
                    {children}
                </ul>
            </div>
        );
    }
}
